import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, ArrowLeft, Upload, Trash2, Users as UsersIcon, CalendarClock, FolderArchive, Award, Pencil, FileText, ExternalLink, Download, ScanLine, XCircle, AlertTriangle, CheckCircle2, Infinity as InfinityIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CAT_LABELS = { formazione: "Formazione", documentale: "Documentale", impianti: "Impianti", sanitaria: "Sanitaria", analisi: "Analisi", consulenza: "Consulenza" };

function daysBetween(iso) {
  if (!iso) return null;
  const d = new Date(iso.slice(0, 10));
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / (1000 * 60 * 60 * 24));
}

export default function CompanyDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [company, setCompany] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const fileRef = useRef(null);

  const isSuper = user?.role === "super_admin";
  const canEdit = isSuper || user?.role === "operatore";

  const load = useCallback(() => {
    api.get(`/companies/${id}`).then((r) => setCompany(r.data));
    api.get(`/workers?company_id=${id}`).then((r) => setWorkers(r.data));
    api.get(`/deadlines?company_id=${id}`).then((r) => setDeadlines(r.data));
    api.get(`/documents?company_id=${id}`).then((r) => setDocuments(r.data));
    api.get(`/certificates?company_id=${id}`).then((r) => setCertificates(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/companies/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(lang === "it" ? "Logo caricato" : "Logo uploaded");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const removeLogo = async () => {
    if (!window.confirm(lang === "it" ? "Rimuovere il logo?" : "Remove logo?")) return;
    await api.delete(`/companies/${id}/logo`);
    load();
  };

  const openEdit = () => {
    setForm({
      name: company.name || "",
      vat_number: company.vat_number || "",
      tax_code: company.tax_code || "",
      address: company.address || "",
      email: company.email || "",
      emails_text: (company.emails || []).join(", "),
      phone: company.phone || "",
      referent: company.referent || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const emails = (form.emails_text || "")
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes("@"));
      // Build partial payload — never send logo_url from this dialog (managed separately)
      const payload = {
        name: form.name,
        vat_number: form.vat_number || null,
        tax_code: form.tax_code || null,
        address: form.address || null,
        email: form.email || null,
        emails,
        phone: form.phone || null,
        referent: form.referent || null,
      };
      await api.put(`/companies/${id}`, payload);
      toast.success(lang === "it" ? "Anagrafica aggiornata" : "Company updated");
      setEditOpen(false);
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail ? JSON.stringify(detail) : (err.message || "Errore salvataggio"));
      toast.error(msg);
      // Keep dialog open so user can retry / correct
    }
  };

  if (!company) return <div className="text-muted-foreground">{t("loading")}</div>;

  const logoSrc = company.logo_url && company.logo_url.startsWith("/") ? `${BACKEND}${company.logo_url}` : company.logo_url;
  const allEmails = [company.email, ...(company.emails || [])].filter(Boolean);

  return (
    <div className="space-y-8">
      <Link to="/companies" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-3.5 h-3.5" /> {t("companies")}
      </Link>

      {/* Header */}
      <div className="flex items-start gap-6 flex-wrap">
        <div className="w-24 h-24 rounded-lg bg-muted grid place-items-center overflow-hidden border border-border">
          {logoSrc ? <img src={logoSrc} alt="" className="w-full h-full object-contain p-2 bg-white" /> : <Building2 className="w-10 h-10 text-muted-foreground" />}
        </div>
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-3xl font-bold tracking-tight">{company.name}</h1>
          <div className="text-sm text-muted-foreground mt-1">P.IVA {company.vat_number || "—"} · CF {company.tax_code || "—"}</div>
          {company.address && <div className="text-sm text-muted-foreground mt-0.5">{company.address}</div>}
          {company.phone && <div className="text-sm text-muted-foreground mt-0.5">📞 {company.phone}</div>}
          {allEmails.length > 0 && (
            <div className="text-sm text-muted-foreground mt-2">
              <div className="text-[10px] uppercase tracking-widest">Email promemoria</div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {allEmails.map((e, i) => (
                  <span key={e} className={`text-xs px-2 py-0.5 rounded-full ${i === 0 ? "bg-primary/10 text-primary" : "bg-muted"}`}>{e}</span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 items-start">
          {canEdit && (
            <Link to={`/attestati?company_id=${company.id}`}>
              <Button variant="outline" data-testid="company-import-attestati" className="rounded-full btn-lift">
                <ScanLine className="w-4 h-4 mr-1.5" />
                {lang === "it" ? "Importa attestati" : "Import certificates"}
              </Button>
            </Link>
          )}
          {canEdit && (
            <Button variant="outline" onClick={openEdit} data-testid="edit-company-button" className="rounded-full btn-lift">
              <Pencil className="w-4 h-4 mr-1.5" /> {lang === "it" ? "Modifica anagrafica" : "Edit"}
            </Button>
          )}
          {canEdit && (
            <>
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-logo-button" className="rounded-full btn-lift">
                <Upload className="w-4 h-4 mr-1.5" /> {lang === "it" ? "Logo" : "Logo"}
              </Button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" hidden onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} />
              {company.logo_url && <Button variant="ghost" size="icon" onClick={removeLogo} className="text-destructive rounded-full"><Trash2 className="w-4 h-4" /></Button>}
            </>
          )}
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          [UsersIcon, t("workers"), workers.length, "text-secondary bg-secondary/10"],
          [Award, t("certificates"), certificates.length, "text-primary bg-primary/10"],
          [FolderArchive, t("documents"), documents.length, "text-[hsl(40,88%,35%)] bg-[hsl(45,96%,88%)]"],
          [CalendarClock, t("deadlines"), deadlines.length, "text-[hsl(0,70%,40%)] bg-[hsl(0,90%,94%)]"],
        ].map(([Ic, label, val, cls]) => (
          <div key={label} className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-md grid place-items-center ${cls}`}><Ic className="w-5 h-5" /></div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
              <div className="text-xl font-bold">{val}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Panoramica dashboard aziendale */}
      {(() => {
        const dlStats = { expired: 0, warning: 0, ok: 0, no_term: 0, completed: 0 };
        deadlines.forEach((d) => {
          const st = d.status || "ok";
          if (st === "expired") dlStats.expired++;
          else if (st === "warning") dlStats.warning++;
          else if (st === "no_term") dlStats.no_term++;
          else if (st === "completed") dlStats.completed++;
          else dlStats.ok++;
        });
        // Per-worker completeness
        const perWorker = workers.map((w) => {
          const wDeadlines = deadlines.filter((d) => d.worker_id === w.id);
          const wDocs = documents.filter((d) => d.worker_id === w.id);
          const expired = wDeadlines.filter((d) => d.status === "expired").length;
          const warning = wDeadlines.filter((d) => d.status === "warning").length;
          return { w, deadlines: wDeadlines.length, docs: wDocs.length, expired, warning };
        }).sort((a, b) => (b.expired + b.warning) - (a.expired + a.warning));
        const critici = perWorker.filter((r) => r.expired + r.warning > 0);
        return (
          <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="company-dashboard">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h2 className="text-lg font-semibold">{lang === "it" ? "Panoramica aziendale" : "Company overview"}</h2>
              <div className="flex gap-2 flex-wrap">
                {dlStats.expired > 0 && <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-rose-100 text-rose-800 border border-rose-200"><XCircle className="w-3 h-3" /> Scadute: {dlStats.expired}</span>}
                {dlStats.warning > 0 && <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200"><AlertTriangle className="w-3 h-3" /> In scadenza: {dlStats.warning}</span>}
                {dlStats.ok > 0 && <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200"><CheckCircle2 className="w-3 h-3" /> Attive: {dlStats.ok}</span>}
                {dlStats.no_term > 0 && <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-sky-100 text-sky-800 border border-sky-200"><InfinityIcon className="w-3 h-3" /> Senza termine: {dlStats.no_term}</span>}
                {dlStats.completed > 0 && <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200"><CheckCircle2 className="w-3 h-3" /> Completate: {dlStats.completed}</span>}
              </div>
            </div>
            {workers.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">{lang === "it" ? "Aggiungi lavoratori per vedere la panoramica." : "Add workers to see the overview."}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left py-2 pr-3">Lavoratore</th>
                      <th className="text-left py-2 pr-3">Mansione</th>
                      <th className="text-right py-2 px-3">Scadenze</th>
                      <th className="text-right py-2 px-3">Documenti</th>
                      <th className="text-right py-2 pr-3">Stato</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perWorker.map((r) => (
                      <tr key={r.w.id} className="border-t border-border hover:bg-muted/40">
                        <td className="py-2 pr-3">
                          <Link to={`/workers/${r.w.id}/documents`} className="text-primary hover:underline font-medium">
                            {r.w.last_name} {r.w.first_name}
                          </Link>
                        </td>
                        <td className="py-2 pr-3 text-muted-foreground">{r.w.role || "—"}</td>
                        <td className="py-2 px-3 text-right">{r.deadlines}</td>
                        <td className="py-2 px-3 text-right">{r.docs}</td>
                        <td className="py-2 pr-3 text-right">
                          {r.expired > 0 && <span className="inline-flex items-center gap-1 text-[10px] uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200 mr-1"><XCircle className="w-3 h-3" /> {r.expired}</span>}
                          {r.warning > 0 && <span className="inline-flex items-center gap-1 text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 mr-1"><AlertTriangle className="w-3 h-3" /> {r.warning}</span>}
                          {r.expired === 0 && r.warning === 0 && <span className="inline-flex items-center gap-1 text-[10px] uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200"><CheckCircle2 className="w-3 h-3" /> OK</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {critici.length > 0 && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    <b className="text-rose-700">{critici.length}</b> lavorator{critici.length === 1 ? "e" : "i"} con almeno una scadenza critica.
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Workers */}
        <Section title={t("workers")} count={workers.length} link="/workers">
          {workers.map((w) => (
            <li key={w.id} className="px-5 py-3 text-sm flex justify-between">
              <span className="font-medium">{w.last_name} {w.first_name}</span>
              <span className="text-muted-foreground">{w.role || "—"}</span>
            </li>
          ))}
          {workers.length === 0 && <li className="p-5 text-sm text-muted-foreground">{t("no_data")}</li>}
        </Section>

        {/* Certificates */}
        <Section title={t("certificates")} count={certificates.length} link="/certificates">
          {certificates.map((c) => (
            <li key={c.id} className="px-5 py-3 text-sm flex items-center gap-3">
              <Award className="w-4 h-4 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{c.course_title}</div>
                <div className="text-xs text-muted-foreground">{c.number} · {c.issue_date}</div>
              </div>
              <a href={`${BACKEND}${c.pdf_url}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> PDF
              </a>
            </li>
          ))}
          {certificates.length === 0 && <li className="p-5 text-sm text-muted-foreground">{t("no_data")}</li>}
        </Section>

        {/* Documents */}
        <Section title={t("documents")} count={documents.length} link="/documents">
          {documents.map((d) => (
            <li key={d.id} className="px-5 py-3 text-sm flex items-center gap-3">
              <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{d.name}</div>
                <div className="text-xs text-muted-foreground">{d.category} · v{d.version}</div>
              </div>
              <a href={`${BACKEND}${d.file_url}`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> {t("view")}
              </a>
            </li>
          ))}
          {documents.length === 0 && <li className="p-5 text-sm text-muted-foreground">{t("no_data")}</li>}
        </Section>

        {/* Deadlines — ALL categories (formazione, documentale, impianti, sanitaria, analisi) */}
        <Section title={t("deadlines")} count={deadlines.length} link="/deadlines">
          {deadlines.map((d) => {
            const days = daysBetween(d.due_date);
            return (
              <li key={d.id} className={`px-5 py-3 severity-${d.status}`}>
                <div className="flex justify-between text-sm">
                  <span className="font-medium truncate flex items-center gap-2">
                    {d.title}
                    {d.subtype_label && <span className="text-[9px] font-mono bg-muted text-muted-foreground px-1 rounded">{d.subtype_label}</span>}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded badge-${d.status}`}>{d.due_date?.slice(0, 10) || "—"}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                  <span>{CAT_LABELS[d.category] || d.category}</span>
                  {days != null && d.status !== "completed" && d.status !== "no_term" && (
                    <span>· {days >= 0 ? days : -days} {t("days_left")}</span>
                  )}
                  {d.attachment_url && (
                    <a href={`${BACKEND}${d.attachment_url}`} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                      <FileText className="w-3 h-3" /> {lang === "it" ? "Allegato" : "File"}
                    </a>
                  )}
                </div>
              </li>
            );
          })}
          {deadlines.length === 0 && <li className="p-5 text-sm text-muted-foreground">{t("no_data")}</li>}
        </Section>
      </div>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{lang === "it" ? "Modifica anagrafica" : "Edit company"}</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>{t("company_name")}</Label><Input required value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>{t("vat_number")}</Label><Input value={form.vat_number || ""} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
            <div><Label>{t("tax_code")}</Label><Input value={form.tax_code || ""} onChange={(e) => setForm({ ...form, tax_code: e.target.value })} /></div>
            <div className="col-span-2"><Label>{t("address")}</Label><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>{t("email")} <span className="text-muted-foreground text-[10px]">(primaria)</span></Label><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>{t("phone")}</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>{lang === "it" ? "Altre email per promemoria" : "Additional emails"}</Label>
              <Textarea rows={2} value={form.emails_text || ""} onChange={(e) => setForm({ ...form, emails_text: e.target.value })} placeholder="ufficio@azienda.it, sicurezza@azienda.it" data-testid="edit-company-emails" />
              <p className="text-[11px] text-muted-foreground mt-1">{lang === "it" ? "Separate da virgola o riga. Tutte riceveranno i promemoria (con BCC a Sicurgarda)." : "Comma/newline separated."}</p>
            </div>
            <div><Label>{t("referent")}</Label><Input value={form.referent || ""} onChange={(e) => setForm({ ...form, referent: e.target.value })} /></div>
            <DialogFooter className="col-span-2">
              <Button type="submit" className="rounded-full btn-lift" data-testid="save-company-button">{t("save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, count, link, children }) {
  return (
    <section className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <h2 className="font-semibold">{title} <span className="text-muted-foreground text-sm">({count})</span></h2>
        <Link to={link} className="text-xs uppercase tracking-widest text-primary">↗</Link>
      </div>
      <ul className="divide-y divide-border max-h-80 overflow-y-auto">{children}</ul>
    </section>
  );
}
